import { IonButton, IonIcon, IonImg, IonMenuToggle } from '@ionic/react';
import { arrowBack, ellipsisVertical, informationCircleOutline } from 'ionicons/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { differenceInYears, format, parseISO } from 'date-fns';
import { useForm } from 'react-hook-form';
import { lastValueFrom } from 'rxjs';
import { useDispatch, useSelector } from 'react-redux';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ICasinoSchema } from 'appRedux/models/casinoModels';
import { casinoBgMap } from 'components/CasinoCard.tsx/CasinoCard';
import { casinoIdListEnum } from 'common/commonEnum';

const casinoLogoWhiteMap: Partial<Record<casinoIdListEnum, string>> = {
  [casinoIdListEnum.LOTC]: 'assets/images/LOT_property_logo.png',
  [casinoIdListEnum.EUREKA]: 'assets/images/Eureka-White.svg',
  [casinoIdListEnum.RWC]: 'assets/images/RedWindCasino_LogoWhite.svg',
  [casinoIdListEnum.SSC]: 'assets/images/SilverSevens_LogoWhite.svg',
  [casinoIdListEnum.ACH]: 'assets/images/ACHLogo_white.svg',
};
import { AuthService } from 'services/auth';
import { IState, useAppSelector } from 'appRedux/createStore';
import { CasinoReduxCommandCreator } from 'appRedux/actions/casinoCommandCreator';
import { PlayerService } from 'services/player';
import SmartLoading from 'components/SmartLoading';
import { CustomerData, IUserSubscription, SubscriptionData } from '@interfaces/userSubscription';
import { checkProUser } from 'common/common';
import { ReduxCommandCreator } from 'appRedux/actions';
import { FilterReduxCommandCreator } from 'appRedux/actions/filterCommandCreator';
import { markPlayerLinkPending } from 'common/playerLinkedStorage';
import LinkPlayerInfoModal from './LinkPlayerInfoModal';
import LinkPlayerResultModal from './LinkPlayerResultModal';
import {
  bustLinkedAccountsCache,
  linkedAccountsBucket,
  playerScopedKey,
  sharedPlayerScopedGet,
} from 'lib/playerScopedListCache';

interface LinkPlayerCardModalProps {
  casinoInfo: ICasinoSchema;
  onSubmitSuccess: () => void;
  onCancel: () => void;
  IsShowMaybeLater?: boolean;
  /**
   * 'screen' (default): today's full-screen step (header with the casino logo, coin art).
   * 'drawer': the same form, copy and submit logic inside the Explore drawer
   * (LinkPlayerflowVersion 2): no casino header/logo/tag (the casino header above
   * already shows it), no coin art, and no navigation of its own; the drawer closes
   * through onCancel / onSubmitSuccess.
   */
  variant?: 'screen' | 'drawer';
}

interface FormData {
  playerId: string;
  birthday: string;
}

export const validatePlayerId = yup.object().shape({
  playerId: yup.string().required('Player ID is required'),
  birthday: yup
    .string()
    .test('dateOfBirth', 'You must be 18 years or older', (value) => {
      if (!value) {
        return false; // Fail validation if value is undefined or empty
      }

      const formattedDate = format(parseISO(value), 'yyyy-MM-dd');
      return differenceInYears(new Date(), new Date(formattedDate)) >= 18;
    })
    .required('Birthday is required'),
});

const pollInterval = 30000;
const maxPolls = 10;

export const LinkPlayerCardModal: React.FC<LinkPlayerCardModalProps> = ({
  casinoInfo,
  onSubmitSuccess,
  onCancel,
  IsShowMaybeLater = true,
  variant = 'screen',
}: LinkPlayerCardModalProps) => {
  const isDrawer = variant === 'drawer';
  const authService = new AuthService();
  const playerService = new PlayerService();

  const dispatch = useDispatch();
  const history = useHistory();
  const casinoCommands = CasinoReduxCommandCreator(dispatch);
  const commands = ReduxCommandCreator(dispatch);
  const filterCommandCreator = FilterReduxCommandCreator(dispatch);

  const [linkPlayerLoading, setLinkPlayerLoading] = useState(false);
  const [resultType, setResultType] = useState<'success' | 'error' | null>(null);
  const [showInfo, setShowInfo] = useState(false);

  const userId = useSelector<IState>(
    (state) => state.app.persistedState.userInfo?.appUserPk
  ) as string;
  const casinoSchema = useAppSelector((state) => state.app.persistedState.casinoSchema);

  const {
    handleSubmit,
    register,
    formState: { errors },
    trigger,
  } = useForm<FormData>({
    resolver: yupResolver(validatePlayerId),
    defaultValues: {},
  });
  const checkProStatus = async (): Promise<boolean> => {
    try {
      return await new Promise<boolean>((resolve) => {
        authService.GetUserSubscriptions().subscribe({
          next(data) {
            let isProUser = false;
            if (data) {
              const filterData = data.DataSet.filter((item) => item.hasOwnProperty('casinoId'));
              const userSubscriptionCasino = filterData.find(
                (casino: IUserSubscription) => casino.casinoId === 4
              );
              if (userSubscriptionCasino) {
                const {
                  subscriptionData = {} as SubscriptionData,
                  customerData = {} as CustomerData,
                } = userSubscriptionCasino;
                const subscriptionId = subscriptionData.id || '';
                const customerId = customerData.id || '';
                const canceledAt = subscriptionData.canceled_at ?? null;
                const currentPeriodEnd = subscriptionData.current_period_end ?? 0;
                const subscriptionActive = subscriptionData.status || 'inactive';
                const planId = subscriptionData.plan?.id || '';
                isProUser = checkProUser(subscriptionActive, currentPeriodEnd);
                filterCommandCreator.ResetAllFilters();
                commands.SetUserLevel({
                  level: planId,
                  customer_id: customerId,
                  subscription_id: subscriptionId,
                  current_period_end: currentPeriodEnd,
                  canceled_at: canceledAt,
                  isProUser,
                });
              } else {
                commands.SetUserLevel({
                  level: '',
                  customer_id: '',
                  subscription_id: '',
                  isProUser: false,
                });
              }
            }
            resolve(isProUser);
          },
          error(err) {
            resolve(false);
          },
        });
      });
    } catch (e) {
      return false;
    }
  };

  const onSubmit = async (data: FormData): Promise<void> => {
    setResultType(null);

    try {
      const isValid = await trigger();

      if (isValid) {
        setLinkPlayerLoading(true);
        const formattedDate = format(parseISO(data.birthday), 'yyyy-MM-dd').replace(/-/g, '-');

        await playerService
          .LinkPlayerCard(casinoInfo.casinoId, userId, data.playerId, formattedDate)
          .toPromise()
          .then(async (response) => {
            if (response && response?.errorMsg) {
              setResultType('error');
              return;
            }

            // Record that the user added a player ID to this casino, so the
            // "Player ID Linked!" notification only fires for casinos they
            // actually linked here (once verification completes).
            markPlayerLinkPending(casinoInfo.casinoId);

            bustLinkedAccountsCache(userId);
            const linkedResponse = await lastValueFrom(
              sharedPlayerScopedGet(linkedAccountsBucket, playerScopedKey(userId), () =>
                authService.GetLinkedAccounts(userId)
              )
            );
            const linkedAccounts = linkedResponse?.user.linkedAccounts;
            if (linkedAccounts) {
              casinoCommands.SetLinkedCasinos({
                casinoLinkedAccounts: linkedAccounts,
              });
            }
            setLinkPlayerLoading(false);
            setResultType('success');

            void (async () => {
              const isProAfterLink = await checkProStatus();
              if (!isProAfterLink) {
                for (let pollCount = 0; pollCount < maxPolls; pollCount++) {
                  const isPro = await checkProStatus();
                  if (isPro) break;
                  await new Promise((resolve) => setTimeout(resolve, pollInterval));
                }
              }
            })();
          });
      } else {
        setResultType('error');
      }
    } catch (error) {
      setResultType('error');
    } finally {
      setLinkPlayerLoading(false);
    }
  };

  const logoSrc =
    casinoLogoWhiteMap[casinoInfo?.casinoId as casinoIdListEnum] ?? casinoInfo?.logos?.logoAlt;
  const headerBg = casinoBgMap[casinoInfo?.casinoId as casinoIdListEnum];
  const headerStyle: React.CSSProperties | undefined = headerBg
    ? {
        backgroundImage: `url(/${headerBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : undefined;

  return (
    <div
      className={isDrawer ? 'link-player-drawer-content' : 'link-player-overlay'}
      data-testid={isDrawer ? 'linkCardDrawer' : 'linkCardModal'}
    >
      <SmartLoading loading={linkPlayerLoading} />

      {!isDrawer && (
        <div className="link-player-dark__header" style={headerStyle}>
          <button className="link-player-dark__back-btn" onClick={onCancel} aria-label="Back">
            <IonImg src="assets/images/back-arrow.svg" alt="Back" />
          </button>
          {logoSrc && (
            <div className="link-player-dark__logo-wrap">
              <IonImg
                src={logoSrc}
                alt={casinoInfo.displayName ?? 'Casino'}
                className={`link-player-dark__logo${casinoInfo.kpCasinoPk === casinoIdListEnum.ACH ? ' link-player-dark__logo--ach' : ''}`}
              />
            </div>
          )}
          <IonMenuToggle>
            <button className="link-player-dark__menu-btn" aria-label="Menu">
              <IonIcon icon={ellipsisVertical} />
            </button>
          </IonMenuToggle>
        </div>
      )}

      <div className="link-player-dark__body">
        <div className="link-player-dark__heading-group">
          <div className="link-player-dark__title-row">
            <h2 className="link-player-dark__title">
              LINK <span className="link-player-dark__title--gold">PLAYER ID</span>
            </h2>
            <IonIcon
              icon={informationCircleOutline}
              className="link-player-dark__info-icon"
              onClick={() => setShowInfo(true)}
              role="button"
              aria-label="Learn about Player ID"
            />
          </div>

          <p className="link-player-dark__subtitle">
            Gain full access to all of Slot Check&apos;s features at this casino!
          </p>
        </div>

        <form
          className="link-player-dark__form"
          onSubmit={(event) => {
            event.preventDefault();
            handleSubmit(onSubmit)().catch((e) => {
              console.error(e);
            });
          }}
        >
          <div className="link-player-dark__form-glass">
            <div className="link-player-dark__field-group">
              <label className="link-player-dark__label">PLAYER ID</label>
              <div className="link-player-dark__input-wrap">
                <input
                  className="link-player-dark__input"
                  placeholder="Enter Player ID"
                  data-testid="playerId-input"
                  {...register('playerId', { required: 'This is a required field' })}
                />
                {errors.playerId?.message && (
                  <span className="link-player-dark__field-error" data-testid="playerId-error">
                    {errors.playerId.message}
                  </span>
                )}
              </div>
            </div>

            <div className="link-player-dark__field-group">
              <label className="link-player-dark__label">BIRTHDAY</label>
              <div className="link-player-dark__input-wrap">
                <input
                  className="link-player-dark__input"
                  type="date"
                  placeholder="MM/DD/YYYY"
                  data-testid="birthday-input"
                  {...register('birthday', { required: 'Birthday is required' })}
                />
                {errors.birthday?.message && (
                  <span className="link-player-dark__field-error" data-testid="dob-error">
                    {errors.birthday.message}
                  </span>
                )}
              </div>
            </div>

            <p className="link-player-dark__legal">
              By linking your{' '}
              <span className="link-player-dark__legal-link" onClick={() => setShowInfo(true)}>
                Player ID
              </span>
              , you authorize Slot Check to verify the above information with the casino. Once
              verification is complete, you will be able to play like a PRO.
            </p>
          </div>

          <IonButton
            expand="block"
            type="submit"
            className="link-player-dark__cta-btn"
            data-testid="submitButton"
          >
            LINK PLAYER ID
          </IonButton>

          {IsShowMaybeLater && (
            <IonButton
              expand="block"
              fill="clear"
              className="link-player-dark__maybe-later-btn"
              onClick={onCancel}
            >
              MAYBE LATER
            </IonButton>
          )}
        </form>
      </div>

      {!isDrawer && (
        <div className="link-player-dark__bottom-img">
          <IonImg src="assets/images/bgcoin-linkplayer.png" alt="" aria-hidden="true" />
        </div>
      )}

      {resultType && (
        <LinkPlayerResultModal
          type={resultType}
          onContinue={() => {
            if (resultType === 'success') {
              // The drawer is already on Explore: it just closes into it.
              if (!isDrawer) history.replace('/tabs/home');
              onSubmitSuccess();
            } else {
              setResultType(null);
            }
          }}
          onTryAgain={() => setResultType(null)}
        />
      )}

      {showInfo && (
        <LinkPlayerInfoModal
          onLinkPlayerClick={() => setShowInfo(false)}
          onMaybeLater={() => {
            setShowInfo(false);
            if (isDrawer) {
              onCancel();
            } else {
              history.replace('/tabs/home');
            }
          }}
        />
      )}
    </div>
  );
};

export default LinkPlayerCardModal;
